import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Paper,
  TextField,
  Button,
  Tabs,
  Tab,
  MenuItem,
  CircularProgress,
  Divider,
} from "@material-ui/core";
import { SaveRounded as SaveIcon } from "@material-ui/icons";
import { toast } from "react-toastify";

import { i18n } from "../../../translate/i18n";
import {
  listPages,
  savePage,
  getSettings,
  saveSettings,
} from "../../../services/api/siteContent";

// ───── Design tokens
const SP = { xxs: 4, xs: 8, sm: 12, md: 16, lg: 20, xl: 24, xxl: 32, hero: 48 };
const TT = { min: 44, primary: 48, hero: 56 };
const TYPE = {
  caption:    { fontSize: 12, lineHeight: 1.4, letterSpacing: 0.2 },
  body:       { fontSize: 14, lineHeight: 1.5 },
  bodyStrong: { fontSize: 15, lineHeight: 1.45, fontWeight: 500 },
  subheading: { fontSize: 17, lineHeight: 1.4,  fontWeight: 500 },
  heading:    { fontSize: 20, lineHeight: 1.3,  fontWeight: 600 },
};
const RAD = { pill: 999, sm: 8, md: 12, lg: 16, xl: 20 };
const COL = {
  brandGreen:     "#25d366",
  brandGreenDeep: "#128c7e",
  brandGreenSoft: "rgba(37,211,102,0.08)",
  textPrimary:    "#0B0D0C",
  textSecondary:  "#52525b",
  borderSoft:     "#eaeaea",
  pageBg:         "#f7faf8",
  cardBg:         "#ffffff",
  destructive:    "#ef4444",
  warning:        "#f59e0b",
};

const LANGUAGES = [
  { code: "he", label: "עברית", dir: "rtl" },
  { code: "en", label: "English", dir: "ltr" },
  { code: "ru", label: "Русский", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
  { code: "es", label: "Español", dir: "ltr" },
];

const META_TITLE_MAX = 70;
const META_DESC_MAX  = 160;

const MOBILE_INPUT_PROPS = { style: { fontSize: 16 } };
// Per spec B.7 / A.4: input font must be ≥16px on mobile to defeat iOS Safari
// zoom-on-focus. JSON-LD textareas were 14px → triggered zoom on every focus.
// We can't easily breakpoint-gate via inline `inputProps`, so bump to 16px
// everywhere (monospace stays the same on desktop visually). The minor desktop
// size increase is acceptable; the mobile zoom bug is not.
const MONO_INPUT_PROPS = {
  style: {
    fontSize: 16,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    lineHeight: 1.45,
  },
  spellCheck: false,
};

const prettyJson = (value) => {
  if (value === null || value === undefined) return "{}";
  if (typeof value === "string") {
    try { return JSON.stringify(JSON.parse(value), null, 2); } catch { return value; }
  }
  try { return JSON.stringify(value, null, 2); } catch { return "{}"; }
};

const parseJsonOrToast = (raw, fieldLabel) => {
  if (!raw || !raw.trim()) return {};
  try {
    return JSON.parse(raw);
  } catch (err) {
    toast.error(
      i18n.t("siteContent.errors.invalidJson", {
        defaultValue: `Invalid JSON in ${fieldLabel}: ${err.message}`,
        field: fieldLabel,
      })
    );
    return undefined;
  }
};

const EMPTY_TRANSLATION_SEO = {
  metaTitle: "",
  metaDescription: "",
  ogImageUrl: "",
  ogType: "website",
  twitterCard: "summary_large_image",
  twitterImageUrl: "",
  twitterSite: "",
  twitterCreator: "",
  canonicalUrl: "",
  // Keep body/title/contentBlocks empty here — Save Per-Page SEO merges with
  // existing translation rows on the backend's upsert. The PagesEditor owns
  // those fields; SeoEditor only owns the SEO metadata.
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const accent     = isDark ? "#2ee37a" : "#128c7e";
  const accentSoft = isDark ? "rgba(46,227,122,0.08)" : "rgba(18,140,126,0.06)";
  const hair       = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.06)";
  const fg         = isDark ? "rgba(255,255,255,0.92)" : "#0f1614";
  const fgMuted    = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,22,20,0.55)";
  const cardSurface = isDark ? "#13181a" : "#ffffff";

  return {
    root: {
      display: "flex",
      flexDirection: "column",
      gap: SP.lg,
      fontFamily: "'Roboto',system-ui,-apple-system,sans-serif",
      color: fg,
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      // Sticky action bar reserves bottom space so the final field never sits
      // under the floating save bar on mobile.
      paddingBottom: 96,
      [theme.breakpoints.down("xs")]: {
        gap: SP.md,
        paddingBottom: 108,
      },
    },
    // ──────── Section card (matches CRM Paper surface treatment) ──────────
    card: {
      position: "relative",
      background: cardSurface,
      border: `1px solid ${hair}`,
      borderRadius: RAD.lg,
      padding: SP.xl,
      minWidth: 0,
      maxWidth: "100%",
      boxSizing: "border-box",
      boxShadow: isDark
        ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 24px rgba(0,0,0,0.32)"
        : "0 1px 0 rgba(15,22,20,0.02), 0 8px 24px rgba(15,22,20,0.04)",
      transition: "box-shadow 220ms cubic-bezier(0.16, 1, 0.3, 1)",
      "&:hover": {
        boxShadow: isDark
          ? "0 1px 0 rgba(255,255,255,0.05) inset, 0 12px 32px rgba(0,0,0,0.4)"
          : "0 1px 0 rgba(15,22,20,0.02), 0 12px 32px rgba(15,22,20,0.06)",
      },
      [theme.breakpoints.down("xs")]: {
        padding: SP.md,
        borderRadius: RAD.lg,
      },
    },
    // Header block inside each card: accent meta + section title + subtitle
    cardHeader: {
      display: "flex",
      flexDirection: "column",
      gap: SP.xxs,
      marginBottom: SP.lg,
      paddingBottom: SP.md,
      borderBottom: `1px solid ${hair}`,
      [theme.breakpoints.down("xs")]: {
        marginBottom: SP.md,
        paddingBottom: SP.sm,
      },
    },
    accentMeta: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      margin: 0,
      lineHeight: 1.2,
    },
    sectionTitle: {
      ...TYPE.subheading,
      color: fg,
      margin: 0,
      [theme.breakpoints.down("xs")]: {
        fontSize: 16,
      },
    },
    sectionSub: {
      ...TYPE.caption,
      color: fgMuted,
      margin: 0,
      marginTop: SP.xxs,
      maxWidth: 640,
    },
    // Subgroup header for clustering related fields inside a single card
    // (e.g. "META TAGS", "OPEN GRAPH", "TWITTER CARD" within the per-page card)
    subgroupHeader: {
      gridColumn: "1 / -1",
      display: "flex",
      flexDirection: "column",
      gap: 2,
      marginTop: SP.sm,
      paddingTop: SP.md,
      borderTop: `1px solid ${hair}`,
      // first subgroup in the form has no top divider (the cardHeader already
      // separated it from the title block)
      "&:first-child": {
        marginTop: 0,
        paddingTop: 0,
        borderTop: "none",
      },
    },
    subgroupMeta: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      margin: 0,
      lineHeight: 1.2,
    },
    subgroupTitle: {
      fontSize: 13,
      fontWeight: 500,
      color: fgMuted,
      margin: 0,
    },
    form: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: SP.md,
      minWidth: 0,
      // Clamp grid children so intrinsic min-content of TextField inputs
      // (RTL meta description, JSON-LD textarea) can't push the column track
      // past the card width at the sm/md boundary.
      "& > *": {
        minWidth: 0,
        maxWidth: "100%",
        boxSizing: "border-box",
      },
      // Field label uppercase + 0.18em letter-spacing per brief, in fgMuted
      "& .MuiInputLabel-root": {
        textTransform: "uppercase",
        letterSpacing: "0.18em",
        fontSize: 11,
        fontWeight: 500,
        color: fgMuted,
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: accent,
      },
      // Hairline outlines on inputs; brand-green focus ring
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: hair,
        transition: "border-color 160ms ease",
      },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(15,22,20,0.16)",
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: accent,
        borderWidth: 1,
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: RAD.sm,
        background: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,22,20,0.015)",
      },
      "& .MuiFormHelperText-root": {
        fontSize: 11,
        letterSpacing: 0.2,
        color: fgMuted,
        marginInlineStart: 2,
      },
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
      },
      // xs-down: belt-and-suspenders 16px input rule (defeats iOS Safari
      // zoom-on-focus even on TextFields that miss the inputProps prop), plus
      // tighter field padding to reclaim phone width.
      [theme.breakpoints.down("xs")]: {
        gap: SP.sm,
        "& .MuiInputBase-input": { fontSize: 16 },
        "& .MuiOutlinedInput-input": { padding: "12px 12px" },
        "& .MuiInputLabel-root": { fontSize: 10.5 },
      },
    },
    fullRow: { gridColumn: "1 / -1", minWidth: 0, maxWidth: "100%" },
    // ─────────────────── Language tabs (per-page card) ────────────────────
    langTabs: {
      borderBottom: `1px solid ${hair}`,
      marginBottom: SP.lg,
      minHeight: TT.min,
    },
    langTab: {
      ...TYPE.bodyStrong,
      textTransform: "uppercase",
      letterSpacing: "0.14em",
      fontSize: 12,
      minHeight: TT.min,
      minWidth: 0,
      padding: `${SP.xs}px ${SP.md}px`,
      color: fgMuted,
      transition: "color 160ms ease",
      "&:hover": { color: accent },
      "&.Mui-selected": { color: accent, fontWeight: 700 },
      [theme.breakpoints.down("xs")]: {
        fontSize: 11,
        padding: `${SP.xs}px ${SP.sm}px`,
      },
    },
    tabIndicator: {
      background: COL.brandGreen,
      height: 2,
      borderRadius: 2,
    },
    // ─────────────────── Sticky action bar ────────────────────────────────
    // Floats above page content on mobile; sits as a card-internal row on
    // desktop. Either way, primary CTA = WhatsApp green, ≥48px tall.
    saveRow: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: SP.sm,
      marginTop: SP.lg,
      paddingTop: SP.md,
      borderTop: `1px solid ${hair}`,
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column-reverse",
        // Sticky bottom bar on xs — escapes card padding via negative margin
        position: "sticky",
        bottom: 0,
        marginInline: -SP.md,
        marginBottom: -SP.md,
        marginTop: SP.md,
        paddingInline: SP.md,
        paddingBlock: SP.sm,
        background: isDark
          ? "rgba(19,24,26,0.94)"
          : "rgba(255,255,255,0.94)",
        backdropFilter: "saturate(180%) blur(12px)",
        WebkitBackdropFilter: "saturate(180%) blur(12px)",
        borderTop: `1px solid ${hair}`,
        borderBottomLeftRadius: RAD.lg,
        borderBottomRightRadius: RAD.lg,
        zIndex: 2,
      },
    },
    saveBtn: {
      background: COL.brandGreen,
      color: "#ffffff",
      fontWeight: 700,
      fontSize: 14,
      letterSpacing: 0.2,
      textTransform: "none",
      borderRadius: RAD.pill,
      padding: `${SP.sm}px ${SP.xl}px`,
      minHeight: TT.primary,
      boxShadow: "0 2px 8px rgba(37,211,102,0.25)",
      transition: "background 160ms ease, box-shadow 160ms ease, transform 120ms ease",
      "&:hover": {
        background: COL.brandGreenDeep,
        boxShadow: "0 4px 14px rgba(18,140,126,0.32)",
      },
      "&:active": { transform: "scale(0.985)" },
      "&.Mui-disabled": {
        background: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.08)",
        color: fgMuted,
        boxShadow: "none",
      },
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        minHeight: TT.primary,
      },
    },
    cancelBtn: {
      color: fgMuted,
      fontWeight: 500,
      fontSize: 14,
      textTransform: "none",
      borderRadius: RAD.pill,
      padding: `${SP.sm}px ${SP.lg}px`,
      minHeight: TT.primary,
      border: `1px solid ${hair}`,
      background: "transparent",
      "&:hover": {
        background: accentSoft,
        color: accent,
        borderColor: accent,
      },
      [theme.breakpoints.down("xs")]: {
        width: "100%",
      },
    },
    loadingWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: SP.xxl,
      color: fgMuted,
    },
    divider: {
      margin: `${SP.lg}px 0 ${SP.md}px`,
      background: hair,
      height: 1,
    },
    // Per-page header row (page selector + future filters). Token-only, no
    // inline breakpoint values — spec E forbids inline style for responsive.
    perPageHeader: {
      display: "flex",
      gap: SP.md,
      flexWrap: "wrap",
      alignItems: "center",
      marginBottom: SP.lg,
      paddingBottom: SP.md,
      borderBottom: `1px solid ${hair}`,
      minWidth: 0,
      "& .MuiInputLabel-root": {
        textTransform: "uppercase",
        letterSpacing: "0.18em",
        fontSize: 11,
        fontWeight: 500,
        color: fgMuted,
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: hair },
      "& .MuiOutlinedInput-root": {
        borderRadius: RAD.sm,
        background: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,22,20,0.015)",
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: accent,
      },
    },
    // Page selector: 280px floor on desktop, full-width on sm-and-below.
    // Page-list section visually leads the form (perPageHeader precedes the
    // form Box in JSX); order:-1 hardens that even if a wrapper later
    // re-flexes the layout.
    pageSelect: {
      minWidth: 280,
      order: -1,
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        minWidth: 0,
      },
    },
  };
});

// Twitter card types accepted by Twitter's parser. Restricting via select
// avoids the most common typo (singular vs plural).
const TWITTER_CARD_TYPES = [
  "summary",
  "summary_large_image",
  "app",
  "player",
];

// OpenGraph types — pinned to the small set the public site supports.
const OG_TYPES = [
  "website",
  "article",
  "profile",
  "product",
];

const SeoEditor = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [savingSite, setSavingSite] = useState(false);
  const [savingPage, setSavingPage] = useState(false);

  // Site-wide settings
  const [siteForm, setSiteForm] = useState({
    faviconUrl: "",
    faviconIcoUrl: "",
    appleTouchIconUrl: "",
    manifestUrl: "",
    themeColor: "#128c7e",
    jsonLdOrganizationRaw: "{}",
    jsonLdWebSiteRaw: "{}",
    jsonLdSiteNavRaw: "{}",
  });

  // Per-page SEO
  const [pages, setPages] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [langIdx, setLangIdx] = useState(0);
  const [hreflangMapRaw, setHreflangMapRaw] = useState("{}");
  const [seoByLang, setSeoByLang] = useState({});

  const currentLang = LANGUAGES[langIdx]?.code || "he";
  const seoSlot = seoByLang[currentLang] || {
    ...EMPTY_TRANSLATION_SEO,
    jsonLdPageRaw: "{}",
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [pagesRes, settingsRes] = await Promise.all([
          listPages(),
          getSettings(),
        ]);
        if (cancelled) return;
        const list = Array.isArray(pagesRes?.pages) ? pagesRes.pages : [];
        setPages(list);
        if (list.length && !selectedSlug) setSelectedSlug(list[0].slug);
        const s = settingsRes?.settings || {};
        setSiteForm({
          faviconUrl: s.faviconUrl || "",
          faviconIcoUrl: s.faviconIcoUrl || "",
          appleTouchIconUrl: s.appleTouchIconUrl || "",
          manifestUrl: s.manifestUrl || "",
          themeColor: s.themeColor || "#128c7e",
          jsonLdOrganizationRaw: prettyJson(s.jsonLdOrganization ?? {}),
          jsonLdWebSiteRaw: prettyJson(s.jsonLdWebSite ?? {}),
          jsonLdSiteNavRaw: prettyJson(s.jsonLdSiteNav ?? {}),
        });
      } catch (err) {
        toast.error(
          i18n.t("siteContent.errors.loadSeo", {
            defaultValue: "Failed to load SEO data",
          })
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When page selection changes, hydrate per-language SEO state from the
  // already-cached `pages` payload (listPages returns the full row).
  useEffect(() => {
    if (!selectedSlug) return;
    const row = pages.find((p) => p.slug === selectedSlug);
    if (!row) return;
    setHreflangMapRaw(prettyJson(row.hreflangMap ?? {}));
    const next = {};
    for (const lang of LANGUAGES) {
      const t = (row.translations || []).find((x) => x.language === lang.code);
      next[lang.code] = {
        metaTitle: t?.metaTitle || "",
        metaDescription: t?.metaDescription || "",
        ogImageUrl: t?.ogImageUrl || "",
        ogType: t?.ogType || "website",
        twitterCard: t?.twitterCard || "summary_large_image",
        twitterImageUrl: t?.twitterImageUrl || "",
        twitterSite: t?.twitterSite || "",
        twitterCreator: t?.twitterCreator || "",
        canonicalUrl: t?.canonicalUrl || "",
        jsonLdPageRaw: prettyJson(t?.jsonLdPage ?? {}),
        // preserve body/title/contentBlocks so PUT merges without nuking them
        _preservedTitle: t?.title || "",
        _preservedBody: t?.body || "",
        _preservedContentBlocks: t?.contentBlocks ?? {},
      };
    }
    setSeoByLang(next);
  }, [selectedSlug, pages]);

  const patchSeo = useCallback((patch) => {
    setSeoByLang((prev) => ({
      ...prev,
      [currentLang]: {
        ...(prev[currentLang] || EMPTY_TRANSLATION_SEO),
        ...patch,
      },
    }));
  }, [currentLang]);

  const handleSaveSiteWide = useCallback(async () => {
    const org = parseJsonOrToast(siteForm.jsonLdOrganizationRaw, "jsonLdOrganization");
    if (org === undefined) return;
    const ws = parseJsonOrToast(siteForm.jsonLdWebSiteRaw, "jsonLdWebSite");
    if (ws === undefined) return;
    const sn = parseJsonOrToast(siteForm.jsonLdSiteNavRaw, "jsonLdSiteNav");
    if (sn === undefined) return;
    try {
      setSavingSite(true);
      await saveSettings({
        faviconUrl: siteForm.faviconUrl,
        faviconIcoUrl: siteForm.faviconIcoUrl,
        appleTouchIconUrl: siteForm.appleTouchIconUrl,
        manifestUrl: siteForm.manifestUrl,
        themeColor: siteForm.themeColor,
        jsonLdOrganization: org,
        jsonLdWebSite: ws,
        jsonLdSiteNav: sn,
      });
      toast.success(
        i18n.t("siteContent.toasts.settingsSaved", {
          defaultValue: "Site-wide settings saved",
        })
      );
    } catch (err) {
      toast.error(
        i18n.t("siteContent.errors.saveSettings", {
          defaultValue: "Failed to save settings",
        })
      );
    } finally {
      setSavingSite(false);
    }
  }, [siteForm]);

  const handleSavePerPageSeo = useCallback(async () => {
    if (!selectedSlug) return;
    // Parse hreflangMap once.
    const hreflang = parseJsonOrToast(hreflangMapRaw, "hreflangMap");
    if (hreflang === undefined) return;
    // Build payload — for each language, parse jsonLdPage and preserve the
    // body/title/contentBlocks fields owned by PagesEditor (so saving SEO
    // metadata doesn't blank out the page body).
    const translationsPayload = {};
    for (const lang of LANGUAGES) {
      const slot = seoByLang[lang.code];
      if (!slot) continue;
      const jsonLdPage = parseJsonOrToast(
        slot.jsonLdPageRaw,
        `jsonLdPage (${lang.code})`
      );
      if (jsonLdPage === undefined) return;
      translationsPayload[lang.code] = {
        title: slot._preservedTitle || "",
        metaTitle: slot.metaTitle || "",
        metaDescription: slot.metaDescription || "",
        body: slot._preservedBody || "",
        ogImageUrl: slot.ogImageUrl || "",
        ogType: slot.ogType || "website",
        twitterCard: slot.twitterCard || "summary_large_image",
        twitterImageUrl: slot.twitterImageUrl || "",
        twitterSite: slot.twitterSite || "",
        twitterCreator: slot.twitterCreator || "",
        canonicalUrl: slot.canonicalUrl || "",
        jsonLdPage,
        contentBlocks: slot._preservedContentBlocks ?? {},
      };
    }
    try {
      setSavingPage(true);
      const data = await savePage(selectedSlug, {
        hreflangMap: hreflang,
        translations: translationsPayload,
      });
      if (data?.page) {
        setPages((prev) =>
          prev.map((p) => (p.slug === data.page.slug ? data.page : p))
        );
      }
      toast.success(
        i18n.t("siteContent.toasts.seoSaved", {
          defaultValue: "Per-page SEO saved",
        })
      );
    } catch (err) {
      toast.error(
        i18n.t("siteContent.errors.saveSeo", {
          defaultValue: "Failed to save per-page SEO",
        })
      );
    } finally {
      setSavingPage(false);
    }
  }, [selectedSlug, hreflangMapRaw, seoByLang]);

  if (loading) {
    return (
      <div className={classes.loadingWrap}>
        <CircularProgress size={28} style={{ color: COL.brandGreenDeep }} />
      </div>
    );
  }

  const dir = LANGUAGES[langIdx].dir;

  return (
    <div className={classes.root}>
      {/* ── Site-wide settings ─────────────────────────────────────────── */}
      <Paper elevation={0} className={classes.card}>
        <div className={classes.cardHeader}>
          <span className={classes.accentMeta}>
            {i18n.t("siteContent.seo.siteEyebrow", {
              defaultValue: "GLOBAL META",
            })}
          </span>
          <h3 className={classes.sectionTitle}>
            {i18n.t("siteContent.seo.siteTitle", {
              defaultValue: "Site-wide settings",
            })}
          </h3>
          <p className={classes.sectionSub}>
            {i18n.t("siteContent.seo.siteSubtitle", {
              defaultValue:
                "Favicons, manifest, theme color, and global JSON-LD blobs. Used by whapiz.com on every page.",
            })}
          </p>
        </div>

        <Box className={classes.form}>
          <div className={classes.subgroupHeader}>
            <span className={classes.subgroupMeta}>
              {i18n.t("siteContent.seo.iconsEyebrow", {
                defaultValue: "ICONS & MANIFEST",
              })}
            </span>
            <p className={classes.subgroupTitle}>
              {i18n.t("siteContent.seo.iconsSubtitle", {
                defaultValue: "Browser-tab icons, PWA manifest, and theme color",
              })}
            </p>
          </div>
          <TextField
            label={i18n.t("siteContent.seo.faviconUrl", {
              defaultValue: "Favicon URL (SVG/PNG)",
            })}
            value={siteForm.faviconUrl}
            onChange={(e) =>
              setSiteForm((s) => ({ ...s, faviconUrl: e.target.value }))
            }
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
          />
          <TextField
            label={i18n.t("siteContent.seo.faviconIcoUrl", {
              defaultValue: "Favicon URL (.ico fallback)",
            })}
            value={siteForm.faviconIcoUrl}
            onChange={(e) =>
              setSiteForm((s) => ({ ...s, faviconIcoUrl: e.target.value }))
            }
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
          />
          <TextField
            label={i18n.t("siteContent.seo.appleTouchIconUrl", {
              defaultValue: "Apple Touch Icon URL",
            })}
            value={siteForm.appleTouchIconUrl}
            onChange={(e) =>
              setSiteForm((s) => ({ ...s, appleTouchIconUrl: e.target.value }))
            }
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
          />
          <TextField
            label={i18n.t("siteContent.seo.manifestUrl", {
              defaultValue: "Web manifest URL",
            })}
            value={siteForm.manifestUrl}
            onChange={(e) =>
              setSiteForm((s) => ({ ...s, manifestUrl: e.target.value }))
            }
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
          />
          <TextField
            label={i18n.t("siteContent.seo.themeColor", {
              defaultValue: "Theme color (hex)",
            })}
            value={siteForm.themeColor}
            onChange={(e) =>
              setSiteForm((s) => ({ ...s, themeColor: e.target.value }))
            }
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ ...MOBILE_INPUT_PROPS, maxLength: 20 }}
            placeholder="#128c7e"
          />
          <Box />

          <div className={classes.subgroupHeader}>
            <span className={classes.subgroupMeta}>
              {i18n.t("siteContent.seo.jsonLdEyebrow", {
                defaultValue: "STRUCTURED DATA",
              })}
            </span>
            <p className={classes.subgroupTitle}>
              {i18n.t("siteContent.seo.jsonLdSubtitle", {
                defaultValue: "JSON-LD blobs injected on every page",
              })}
            </p>
          </div>

          <TextField
            className={classes.fullRow}
            label={i18n.t("siteContent.seo.jsonLdOrg", {
              defaultValue: "JSON-LD: Organization",
            })}
            value={siteForm.jsonLdOrganizationRaw}
            onChange={(e) =>
              setSiteForm((s) => ({
                ...s,
                jsonLdOrganizationRaw: e.target.value,
              }))
            }
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={6}
            InputLabelProps={{ shrink: true }}
            inputProps={MONO_INPUT_PROPS}
          />
          <TextField
            className={classes.fullRow}
            label={i18n.t("siteContent.seo.jsonLdWebSite", {
              defaultValue: "JSON-LD: WebSite",
            })}
            value={siteForm.jsonLdWebSiteRaw}
            onChange={(e) =>
              setSiteForm((s) => ({
                ...s,
                jsonLdWebSiteRaw: e.target.value,
              }))
            }
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={6}
            InputLabelProps={{ shrink: true }}
            inputProps={MONO_INPUT_PROPS}
          />
          <TextField
            className={classes.fullRow}
            label={i18n.t("siteContent.seo.jsonLdSiteNav", {
              defaultValue: "JSON-LD: SiteNavigationElement",
            })}
            value={siteForm.jsonLdSiteNavRaw}
            onChange={(e) =>
              setSiteForm((s) => ({
                ...s,
                jsonLdSiteNavRaw: e.target.value,
              }))
            }
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={6}
            InputLabelProps={{ shrink: true }}
            inputProps={MONO_INPUT_PROPS}
          />
        </Box>

        <div className={classes.saveRow}>
          <Button
            onClick={handleSaveSiteWide}
            className={classes.saveBtn}
            disabled={savingSite}
            startIcon={
              savingSite ? (
                <CircularProgress size={16} style={{ color: "#fff" }} />
              ) : (
                <SaveIcon />
              )
            }
          >
            {savingSite
              ? i18n.t("siteContent.saving", { defaultValue: "Saving…" })
              : i18n.t("siteContent.saveSiteWide", {
                  defaultValue: "Save site-wide settings",
                })}
          </Button>
        </div>
      </Paper>

      {/* ── Per-page SEO ───────────────────────────────────────────────── */}
      <Paper elevation={0} className={classes.card}>
        <div className={classes.cardHeader}>
          <span className={classes.accentMeta}>
            {i18n.t("siteContent.seo.pageEyebrow", {
              defaultValue: "PER-PAGE OVERRIDES",
            })}
          </span>
          <h3 className={classes.sectionTitle}>
            {i18n.t("siteContent.seo.pageTitle", {
              defaultValue: "Per-page SEO",
            })}
          </h3>
          <p className={classes.sectionSub}>
            {i18n.t("siteContent.seo.pageSubtitle", {
              defaultValue:
                "Meta tags, OpenGraph, Twitter card, canonical URL, and hreflang map. Saved with the page; body/title stay editable in the Pages tab.",
            })}
          </p>
        </div>

        <Box className={classes.perPageHeader}>
          <TextField
            select
            label={i18n.t("siteContent.pageSelect.label", {
              defaultValue: "Page",
            })}
            value={selectedSlug}
            onChange={(e) => setSelectedSlug(e.target.value)}
            variant="outlined"
            size="small"
            className={classes.pageSelect}
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
          >
            {pages.length === 0 && (
              <MenuItem value="" disabled>
                {i18n.t("siteContent.pageSelect.empty", {
                  defaultValue: "No pages found",
                })}
              </MenuItem>
            )}
            {pages.map((p) => (
              <MenuItem key={p.slug} value={p.slug}>{p.slug}</MenuItem>
            ))}
          </TextField>
        </Box>

        <Tabs
          value={langIdx}
          onChange={(_e, v) => setLangIdx(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          classes={{ indicator: classes.tabIndicator }}
          className={classes.langTabs}
          aria-label={i18n.t("siteContent.langTabsLabel", {
            defaultValue: "Language",
          })}
        >
          {LANGUAGES.map((l) => (
            <Tab key={l.code} className={classes.langTab} label={l.label} />
          ))}
        </Tabs>

        <Box className={classes.form}>
          <div className={classes.subgroupHeader}>
            <span className={classes.subgroupMeta}>
              {i18n.t("siteContent.seo.metaTagsEyebrow", {
                defaultValue: "META TAGS",
              })}
            </span>
            <p className={classes.subgroupTitle}>
              {i18n.t("siteContent.seo.metaTagsSubtitle", {
                defaultValue: "Title, description, and canonical URL for this language",
              })}
            </p>
          </div>

          <TextField
            label={i18n.t("siteContent.fields.metaTitle", {
              defaultValue: "Meta title",
            })}
            value={seoSlot.metaTitle || ""}
            onChange={(e) => patchSeo({ metaTitle: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ ...MOBILE_INPUT_PROPS, maxLength: META_TITLE_MAX }}
            helperText={`${(seoSlot.metaTitle || "").length}/${META_TITLE_MAX}`}
            dir={dir}
          />
          <TextField
            label={i18n.t("siteContent.seo.canonicalUrl", {
              defaultValue: "Canonical URL",
            })}
            value={seoSlot.canonicalUrl || ""}
            onChange={(e) => patchSeo({ canonicalUrl: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
          />
          <TextField
            className={classes.fullRow}
            label={i18n.t("siteContent.fields.metaDescription", {
              defaultValue: "Meta description",
            })}
            value={seoSlot.metaDescription || ""}
            onChange={(e) => patchSeo({ metaDescription: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={2}
            InputLabelProps={{ shrink: true }}
            inputProps={{ ...MOBILE_INPUT_PROPS, maxLength: META_DESC_MAX }}
            helperText={`${(seoSlot.metaDescription || "").length}/${META_DESC_MAX}`}
            dir={dir}
          />

          <div className={classes.subgroupHeader}>
            <span className={classes.subgroupMeta}>
              {i18n.t("siteContent.seo.openGraphEyebrow", {
                defaultValue: "OPEN GRAPH",
              })}
            </span>
            <p className={classes.subgroupTitle}>
              {i18n.t("siteContent.seo.openGraphSubtitle", {
                defaultValue: "Preview image and content type for social shares",
              })}
            </p>
          </div>

          <TextField
            label={i18n.t("siteContent.seo.ogImageUrl", {
              defaultValue: "OG image URL",
            })}
            value={seoSlot.ogImageUrl || ""}
            onChange={(e) => patchSeo({ ogImageUrl: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
          />
          <TextField
            select
            label={i18n.t("siteContent.seo.ogType", {
              defaultValue: "OG type",
            })}
            value={seoSlot.ogType || "website"}
            onChange={(e) => patchSeo({ ogType: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
          >
            {OG_TYPES.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>

          <div className={classes.subgroupHeader}>
            <span className={classes.subgroupMeta}>
              {i18n.t("siteContent.seo.twitterEyebrow", {
                defaultValue: "TWITTER CARD",
              })}
            </span>
            <p className={classes.subgroupTitle}>
              {i18n.t("siteContent.seo.twitterSubtitle", {
                defaultValue: "Twitter-specific share preview and attribution",
              })}
            </p>
          </div>

          <TextField
            select
            label={i18n.t("siteContent.seo.twitterCard", {
              defaultValue: "Twitter card",
            })}
            value={seoSlot.twitterCard || "summary_large_image"}
            onChange={(e) => patchSeo({ twitterCard: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
          >
            {TWITTER_CARD_TYPES.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>
          <TextField
            label={i18n.t("siteContent.seo.twitterImageUrl", {
              defaultValue: "Twitter image URL",
            })}
            value={seoSlot.twitterImageUrl || ""}
            onChange={(e) => patchSeo({ twitterImageUrl: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
          />

          <TextField
            label={i18n.t("siteContent.seo.twitterSite", {
              defaultValue: "Twitter @site",
            })}
            value={seoSlot.twitterSite || ""}
            onChange={(e) => patchSeo({ twitterSite: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
            placeholder="@whapiz"
          />
          <TextField
            label={i18n.t("siteContent.seo.twitterCreator", {
              defaultValue: "Twitter @creator",
            })}
            value={seoSlot.twitterCreator || ""}
            onChange={(e) => patchSeo({ twitterCreator: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={MOBILE_INPUT_PROPS}
            placeholder="@raviv"
          />

          <div className={classes.subgroupHeader}>
            <span className={classes.subgroupMeta}>
              {i18n.t("siteContent.seo.pageJsonLdEyebrow", {
                defaultValue: "STRUCTURED DATA",
              })}
            </span>
            <p className={classes.subgroupTitle}>
              {i18n.t("siteContent.seo.pageJsonLdSubtitle", {
                defaultValue: "Page-level JSON-LD merged with site-wide blobs",
              })}
            </p>
          </div>

          <TextField
            className={classes.fullRow}
            label={i18n.t("siteContent.seo.jsonLdPage", {
              defaultValue: "JSON-LD: page-specific",
            })}
            value={seoSlot.jsonLdPageRaw || "{}"}
            onChange={(e) => patchSeo({ jsonLdPageRaw: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={6}
            InputLabelProps={{ shrink: true }}
            inputProps={MONO_INPUT_PROPS}
          />
        </Box>

        <Divider className={classes.divider} />

        <TextField
          fullWidth
          label={i18n.t("siteContent.seo.hreflangMap", {
            defaultValue: "hreflang map (JSON, page-level)",
          })}
          value={hreflangMapRaw}
          onChange={(e) => setHreflangMapRaw(e.target.value)}
          variant="outlined"
          size="small"
          multiline
          rows={5}
          InputLabelProps={{ shrink: true }}
          inputProps={MONO_INPUT_PROPS}
          helperText={i18n.t("siteContent.seo.hreflangMapHelp", {
            defaultValue:
              'e.g. { "he": "https://whapiz.com/he/about", "en": "https://whapiz.com/en/about" }',
          })}
        />

        <div className={classes.saveRow}>
          <Button
            onClick={handleSavePerPageSeo}
            className={classes.saveBtn}
            disabled={savingPage || !selectedSlug}
            startIcon={
              savingPage ? (
                <CircularProgress size={16} style={{ color: "#fff" }} />
              ) : (
                <SaveIcon />
              )
            }
          >
            {savingPage
              ? i18n.t("siteContent.saving", { defaultValue: "Saving…" })
              : i18n.t("siteContent.savePerPageSeo", {
                  defaultValue: "Save per-page SEO",
                })}
          </Button>
        </div>
      </Paper>
    </div>
  );
};

export default SeoEditor;
